import { useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, ErrorNotice, Field, Input, Spinner } from '@nw/ui';
import { ApiError, api } from '@nw/web-platform';

type AuthConfig = {
  mode: 'oidc' | 'development';
  developmentPersonas: Array<{
    handle: string;
    displayName: string;
    description: string;
  }>;
};

export function SignInPage() {
  const { returnTo } = useSearch({ from: '/signin' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const config = useQuery({
    queryKey: ['auth-config'],
    queryFn: async () => (await api<AuthConfig>('/auth/config')).data,
  });
  const [handle, setHandle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const safeReturn = returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/';

  const devLogin = async (h: string, displayName?: string) => {
    setBusy(h);
    setError(null);
    try {
      await api('/auth/dev/login', {
        method: 'POST',
        body: {
          handle: h,
          displayName,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });
      qc.clear();
      await navigate({ to: safeReturn });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Sign-in failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="grid min-h-dvh place-items-center px-5 py-12">
      <div className="w-full max-w-md">
        <svg viewBox="0 0 320 60" className="mb-8 h-12 w-full text-[var(--nw-river)]" aria-hidden>
          <path d="M4 40 C 60 10, 100 58, 160 32 S 260 8, 316 28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="nw-river-draw" style={{ ['--nw-path-length' as string]: 400 }} />
        </svg>
        <h1 className="font-display text-5xl leading-[1.05]">Nile Wanderer</h1>
        <p className="mt-3 text-lg text-[var(--nw-quiet)]">Your private world of books, ideas and the threads between them.</p>
        <div className="mt-10">
          {config.isPending ? (
            <Spinner />
          ) : config.isError ? (
            <ErrorNotice title="The workspace server is not responding" detail="Start it with pnpm dev, then try again." onRetry={() => void config.refetch()} />
          ) : config.data.mode === 'oidc' ? (
            <Button asChild variant="primary" size="lg" className="w-full">
              <a href={`/api/v1/auth/oidc/login?returnTo=${encodeURIComponent(safeReturn)}`}>Sign in</a>
            </Button>
          ) : (
            <div className="flex flex-col gap-3">
              <p className="rounded-[var(--nw-radius-panel)] border border-dashed border-[var(--nw-line-strong)] p-3 text-sm text-[var(--nw-quiet)]">
                Development sign-in. This only exists on a local machine and cannot be enabled in production.
              </p>
              {config.data.developmentPersonas.map((p) => (
                <button
                  key={p.handle}
                  type="button"
                  disabled={!!busy}
                  onClick={() => void devLogin(p.handle, p.displayName)}
                  className="flex items-center justify-between gap-4 rounded-[var(--nw-radius-panel)] border border-[var(--nw-line)] bg-[var(--nw-surface)] p-4 text-start transition-colors hover:border-[var(--nw-river)] disabled:opacity-60"
                >
                  <span>
                    <span className="block font-semibold">Continue as {p.displayName}</span>
                    <span className="block text-sm text-[var(--nw-quiet)]">{p.description}</span>
                  </span>
                  {busy === p.handle ? <Spinner label="" /> : null}
                </button>
              ))}
              <form
                className="mt-3 flex items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (handle) void devLogin(handle.toLowerCase());
                }}
              >
                <Field label="Another local identity" hint="Lowercase letters, numbers and dashes" className="flex-1">
                  {(p) => <Input {...p} value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="e.g. second-reader" pattern="[a-z][a-z0-9\-]{1,30}" />}
                </Field>
                <Button type="submit" disabled={!handle || !!busy} className="mb-[1.625rem]">
                  Sign in
                </Button>
              </form>
            </div>
          )}
          {error ? <ErrorNotice className="mt-4" title={error} /> : null}
        </div>
      </div>
    </div>
  );
}
